(() => {
  'use strict';

  const config = {
    clientId: '11699462-98ec-4164-8cc1-2ddda007dc23',
    authorizationEndpoint: 'https://shopify.com/authentication/71869858034/oauth/authorize',
    tokenEndpoint: 'https://shopify.com/authentication/71869858034/oauth/token',
    logoutEndpoint: 'https://shopify.com/authentication/71869858034/logout',
    graphqlEndpoint: 'https://shopify.com/71869858034/account/customer/api/2026-10/graphql',
    callbackPath: '/',
    namespace: 'custom',
    key: 'favorites',
    type: 'list.product_reference',
    scope: 'openid email customer-account-api:full',
  };

  const storageKeys = {
    accessToken: 'cca.favorites.access_token',
    refreshToken: 'cca.favorites.refresh_token',
    expiresAt: 'cca.favorites.expires_at',
    state: 'cca.favorites.oauth_state',
    verifier: 'cca.favorites.oauth_verifier',
    pending: 'cca.favorites.pending_action',
    callback: 'cca.favorites.oauth_callback',
    nonce: 'cca.favorites.oauth_nonce',
    idToken: 'cca.favorites.id_token',
    storefrontIdentitySeen: 'cca.favorites.storefront_identity_seen',
  };

  const productGidPattern = /^gid:\/\/shopify\/Product\/\d+$/;
  const buttons = () => Array.from(document.querySelectorAll('[data-cca-favorite]'));
  const eventElement = (event) => event.target && typeof event.target.closest === 'function' ? event.target : event.target?.parentElement;
  let favoriteGids = new Set();
  let compareDigest = null;
  let customerId = null;
  let busy = false;
  let popupAuth = null;

  function randomString(length = 32) {
    const bytes = new Uint8Array(length);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  function base64Url(bytes) {
    let binary = '';
    bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  }

  async function pkceChallenge(verifier) {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    return base64Url(new Uint8Array(digest));
  }

  function safeReturnPath(value) {
    if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return '/';
    try {
      const url = new URL(value, window.location.origin);
      return url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : '/';
    } catch {
      return '/';
    }
  }

  function callbackUri() {
    return `${window.location.origin}${config.callbackPath}`;
  }

  function scrubOAuthParams() {
    const url = new URL(window.location.href);
    ['code', 'state', 'error', 'error_description', 'error_uri'].forEach((key) => url.searchParams.delete(key));
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }

  function clearSession() {
    Object.values(storageKeys).forEach((key) => sessionStorage.removeItem(key));
    favoriteGids = new Set();
    compareDigest = null;
    customerId = null;
  }

  async function logout() {
    const idToken = sessionStorage.getItem(storageKeys.idToken);
    const returnUrl = `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`;
    clearSession();
    if (!idToken) return;
    try {
      const response = await fetch(`${window.location.origin}/.well-known/openid-configuration`, {headers: {Accept: 'application/json'}});
      if (!response.ok) throw new Error('Shopify logout discovery failed.');
      const discovery = await response.json();
      if (!discovery.end_session_endpoint) throw new Error('Shopify logout endpoint was not provided by discovery.');
      const logoutUrl = new URL(discovery.end_session_endpoint);
      logoutUrl.searchParams.set('id_token_hint', idToken);
      logoutUrl.searchParams.set('post_logout_redirect_uri', returnUrl);
      window.location.assign(logoutUrl.toString());
    } catch (error) {
      console.error('[CCA Favorites]', error);
      showError('You have been signed out of Favorites.');
    }
  }

  function tokenFromStorage() {
    const accessToken = sessionStorage.getItem(storageKeys.accessToken);
    const expiresAt = Number(sessionStorage.getItem(storageKeys.expiresAt) || 0);
    return accessToken && expiresAt > Date.now() + 30000 ? accessToken : null;
  }

  async function saveTokenResponse(payload) {
    if (!payload?.access_token) throw new Error('Shopify login did not return an access token.');
    const expectedNonce = sessionStorage.getItem(storageKeys.nonce);
    if (expectedNonce) {
      if (!payload.id_token) {
        throw new Error(`Shopify login did not return the required ID token (response keys: ${Object.keys(payload).sort().join(', ') || 'none'}).`);
      }
      let claims;
      try {
        const parts = payload.id_token.split('.');
        if (parts.length !== 3 || !parts[1]) throw new Error('JWT payload segment missing');
        const encodedClaims = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        claims = JSON.parse(atob(encodedClaims.padEnd(encodedClaims.length + ((4 - encodedClaims.length % 4) % 4), '=')));
      } catch {
        throw new Error('Shopify login returned an invalid ID token.');
      }
      const audienceMatches = Array.isArray(claims.aud) ? claims.aud.includes(config.clientId) : claims.aud === config.clientId;
      if (claims.nonce !== expectedNonce || !audienceMatches) {
        throw new Error(`Shopify login identity could not be verified (nonce present: ${Boolean(claims.nonce)}, audience match: ${audienceMatches}, issuer present: ${Boolean(claims.iss)}).`);
      }
      sessionStorage.removeItem(storageKeys.nonce);
    }
    sessionStorage.setItem(storageKeys.accessToken, payload.access_token);
    if (payload.id_token) sessionStorage.setItem(storageKeys.idToken, payload.id_token);
    if (payload.refresh_token) sessionStorage.setItem(storageKeys.refreshToken, payload.refresh_token);
    sessionStorage.setItem(storageKeys.expiresAt, String(Date.now() + (Number(payload.expires_in || 3600) * 1000)));
    return payload.access_token;
  }

  async function exchangeCode(code, verifier) {
    const response = await fetch(config.tokenEndpoint, {
      method: 'POST',
      headers: {'Content-Type': 'application/x-www-form-urlencoded'},
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: config.clientId,
        redirect_uri: callbackUri(),
        code,
        code_verifier: verifier,
      }),
    });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload?.error_description || `Shopify login could not be completed (HTTP ${response.status}; response keys: ${Object.keys(payload || {}).sort().join(', ') || 'none'}).`);
    return saveTokenResponse(payload);
  }

  async function refreshToken(refreshToken) {
    const response = await fetch(config.tokenEndpoint, {
      method: 'POST',
      headers: {'Content-Type': 'application/x-www-form-urlencoded'},
      body: new URLSearchParams({grant_type: 'refresh_token', client_id: config.clientId, refresh_token: refreshToken}),
    });
    const payload = await response.json();
    if (!response.ok) {
      clearSession();
      return null;
    }
    return saveTokenResponse(payload);
  }

  async function accessToken() {
    const current = tokenFromStorage();
    if (current) return current;
    const refresh = sessionStorage.getItem(storageKeys.refreshToken);
    return refresh ? refreshToken(refresh) : null;
  }

  async function startLogin(productGid, returnPath) {
    const verifier = randomString();
    const state = randomString();
    const nonce = randomString();
    sessionStorage.setItem(storageKeys.verifier, verifier);
    sessionStorage.setItem(storageKeys.state, state);
    sessionStorage.setItem(storageKeys.nonce, nonce);
    sessionStorage.setItem(storageKeys.pending, JSON.stringify({productGid, returnPath: safeReturnPath(returnPath)}));
    const popup = window.open('', 'cca-favorites-auth', 'popup,width=480,height=720,resizable=yes,scrollbars=yes');
    if (!popup) {
      clearPendingAuthorization();
      showError('Favorites sign-in was blocked. Allow pop-ups for this store and try again.');
      return;
    }
    const challenge = await pkceChallenge(verifier);
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: config.clientId,
      redirect_uri: callbackUri(),
      scope: config.scope,
      state,
      nonce,
      code_challenge: challenge,
      code_challenge_method: 'S256',
    });
    popupAuth = {popup, state, timer: null, timeout: null, consumed: false};
    popup.location.href = `${config.authorizationEndpoint}?${params.toString()}`;
    popupAuth.timer = window.setInterval(() => {
      if (!popupAuth || popupAuth.popup !== popup || !popup.closed) return;
      clearPendingAuthorization();
      showError('Favorites sign-in popup closed or could not reconnect. Try again.');
    }, 250);
    popupAuth.timeout = window.setTimeout(() => {
      if (!popupAuth || popupAuth.popup !== popup) return;
      try { popup.close(); } catch (_) {}
      clearPendingAuthorization();
      showError('Favorites sign-in timed out. Try again.');
    }, 180000);
  }

  function clearPendingAuthorization() {
    if (popupAuth?.timer) window.clearInterval(popupAuth.timer);
    if (popupAuth?.timeout) window.clearTimeout(popupAuth.timeout);
    popupAuth = null;
    [storageKeys.state, storageKeys.verifier, storageKeys.pending, storageKeys.nonce].forEach((key) => sessionStorage.removeItem(key));
    busy = false;
    render();
  }

  async function customerApi(query, variables = {}) {
    let token = await accessToken();
    if (!token) throw new Error('Customer Account API session is unavailable.');
    const request = () => fetch(config.graphqlEndpoint, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Authorization: token},
      body: JSON.stringify({query, variables}),
    });
    let response = await request();
    if (response.status === 401) {
      clearSession();
      throw new Error('Customer Account API returned HTTP 401 for this buyer session.');
    }
    const responseText = await response.text();
    let payload;
    try { payload = JSON.parse(responseText); } catch { throw new Error(`Customer Account API returned non-JSON HTTP ${response.status}.`); }
    if (!response.ok || payload.errors?.length) throw new Error(payload.errors?.map((item) => item.message).join('; ') || 'Favorites request failed.');
    return payload;
  }

  const readQuery = `#graphql
    query ReadFavorites {
      customer {
        id
        metafield(namespace: "${config.namespace}", key: "${config.key}") { value compareDigest }
      }
    }
  `;

  const writeMutation = `#graphql
    mutation WriteFavorites($metafields: [MetafieldsSetInput!]!) {
      metafieldsSet(metafields: $metafields) {
        metafields { namespace key value compareDigest }
        userErrors { field message code }
      }
    }
  `;

  function normalize(value) {
    let parsed = [];
    try { parsed = JSON.parse(value || '[]'); } catch { parsed = []; }
    return [...new Set(Array.isArray(parsed) ? parsed.filter((gid) => productGidPattern.test(gid)) : [])];
  }

  function hydrateStorefrontFavorites() {
    const storefrontCustomerId = document.body?.dataset.ccaStorefrontCustomerId || null;
    const storefrontFavorites = document.body?.dataset.ccaStorefrontFavorites;
    if (!storefrontCustomerId || typeof storefrontFavorites !== 'string') return false;
    favoriteGids = new Set(storefrontFavorites.split(',').map((gid) => gid.trim()).filter((gid) => productGidPattern.test(gid)));
    sessionStorage.setItem(storageKeys.storefrontIdentitySeen, 'true');
    return true;
  }

  async function readFavorites() {
    const storefrontCustomerId = document.body?.dataset.ccaStorefrontCustomerId || null;
    const storefrontIdentitySeen = sessionStorage.getItem(storageKeys.storefrontIdentitySeen) === 'true';
    if (storefrontCustomerId) sessionStorage.setItem(storageKeys.storefrontIdentitySeen, 'true');
    if (storefrontIdentitySeen && !storefrontCustomerId && (tokenFromStorage() || sessionStorage.getItem(storageKeys.refreshToken))) {
      clearSession();
      throw new Error('Shopify customer account is no longer signed in. Please sign in again.');
    }
    const payload = await customerApi(readQuery);
    if (!payload) return false;
    const metafield = payload.data?.customer?.metafield;
    const nextCustomerId = payload.data?.customer?.id || null;
    if (storefrontCustomerId && nextCustomerId && !nextCustomerId.endsWith(`/${storefrontCustomerId}`)) {
      clearSession();
      throw new Error('Shopify storefront and API customer identities do not match.');
    }
    if (customerId && nextCustomerId && customerId !== nextCustomerId) {
      clearSession();
      throw new Error('Shopify customer account changed. Please sign in again.');
    }
    customerId = nextCustomerId;
    if (!customerId) throw new Error('Customer identity could not be verified.');
    favoriteGids = new Set(normalize(metafield?.value));
    compareDigest = metafield?.compareDigest || null;
    return true;
  }

  async function writeFavorites(nextGids, digest) {
    const payload = await customerApi(writeMutation, {
      metafields: [{
        ownerId: customerId,
        namespace: config.namespace,
        key: config.key,
        type: config.type,
        value: JSON.stringify([...new Set(nextGids)]),
        compareDigest: digest,
      }],
    });
    if (!payload) return false;
    const userErrors = payload.data?.metafieldsSet?.userErrors || [];
    if (userErrors.length) throw new Error(userErrors.map((item) => item.message).join('; '));
    const saved = payload.data?.metafieldsSet?.metafields?.[0];
    favoriteGids = new Set(nextGids);
    compareDigest = saved?.compareDigest || null;
    return true;
  }

  function render() {
    buttons().forEach((button) => {
      const saved = favoriteGids.has(button.dataset.productGid);
      button.setAttribute('aria-pressed', String(saved));
      button.classList.toggle('cca-favorite-heart--saved', saved);
      button.disabled = busy;
      button.setAttribute('aria-label', saved ? 'Remove from Favorites' : 'Save to Favorites');
      const icon = button.querySelector('[data-cca-favorite-icon]');
      const label = button.querySelector('[data-cca-favorite-label]');
      if (icon?.tagName.toLowerCase() === 'svg') icon.textContent = '';
      if (icon) icon.textContent = saved ? '♥' : '♡';
      if (label) label.textContent = saved ? 'Remove from Favorites' : 'Save to Favorites';
      if (icon?.tagName.toLowerCase() === 'svg') icon.textContent = '';
    });
  }

  function render() {
    buttons().forEach((button) => {
      const saved = favoriteGids.has(button.dataset.productGid);
      button.setAttribute('aria-pressed', String(saved));
      button.classList.toggle('cca-favorite-heart--saved', saved);
      button.disabled = busy;
      const tooltip = saved ? 'Remove from Favorites' : 'Add to Favorites';
      button.setAttribute('aria-label', tooltip);
      const tooltipNode = ensureTooltip(button);
      if (tooltipNode) {
        tooltipNode.textContent = tooltip;
        button.setAttribute('aria-describedby', tooltipNode.id);
      }
      const label = button.querySelector('[data-cca-favorite-label]');
      if (label) label.textContent = tooltip;
    });
  }

  function tooltipId(button) {
    return `cca-favorite-tooltip-${button.dataset.productGid.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  }

  function ensureTooltip(button) {
    const id = tooltipId(button);
    const documentTooltip = typeof document.getElementById === 'function' ? document.getElementById(id) : null;
    let tooltip = documentTooltip || button.querySelector('[data-cca-favorite-tooltip]');
    if (!tooltip) return null;
    tooltip.id = id;
    if (tooltip.classList?.add) tooltip.classList.add('cca-favorite-floating-tooltip');
    if (document.body?.appendChild && tooltip.parentElement !== document.body) document.body.appendChild(tooltip);
    return tooltip;
  }

  function positionTooltip(button, tooltip) {
    if (!tooltip || tooltip.hidden) return;
    const viewportPadding = 8;
    const gap = 8;
    const width = Math.min(240, Math.max(120, window.innerWidth - viewportPadding * 2));
    const rect = button.getBoundingClientRect();
    tooltip.style.width = `${width}px`;
    tooltip.style.maxWidth = `${width}px`;
    const height = tooltip.offsetHeight || 32;
    const left = Math.min(
      Math.max(viewportPadding, rect.left + (rect.width / 2) - (width / 2)),
      Math.max(viewportPadding, window.innerWidth - width - viewportPadding),
    );
    const above = rect.top - height - gap;
    const below = rect.bottom + gap;
    const top = above >= viewportPadding
      ? above
      : Math.min(below, Math.max(viewportPadding, window.innerHeight - height - viewportPadding));
    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${Math.max(viewportPadding, top)}px`;
  }

  function showTooltip(button) {
    const tooltip = ensureTooltip(button);
    if (!tooltip) return;
    tooltip.textContent = button.getAttribute('aria-label') || 'Favorites';
    tooltip.hidden = false;
    positionTooltip(button, tooltip);
  }

  function hideTooltip(button) {
    const tooltip = ensureTooltip(button);
    if (tooltip) tooltip.hidden = true;
  }

  function repositionVisibleTooltips() {
    buttons().forEach((button) => positionTooltip(button, ensureTooltip(button)));
  }

  function showError(message) {
    buttons().forEach((button) => {
      const status = button.querySelector('[data-cca-favorite-status]');
      if (status) status.textContent = message;
    });
  }

  async function toggle(button) {
    if (busy) return;
    const productGid = button.dataset.productGid;
    if (!productGidPattern.test(productGid)) return;
    busy = true;
    render();
    try {
      if (!tokenFromStorage() && !sessionStorage.getItem(storageKeys.refreshToken)) {
        const returnPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        await startLogin(productGid, returnPath);
        return;
      }
      await readFavorites();
      const next = new Set(favoriteGids);
      if (next.has(productGid)) next.delete(productGid); else next.add(productGid);
      await writeFavorites([...next], compareDigest);
    } catch (error) {
      console.error('[CCA Favorites]', error);
      showError(error?.message || 'Favorites are temporarily unavailable.');
      button.setAttribute('data-cca-favorite-error', 'true');
    } finally {
      busy = false;
      render();
    }
  }

  async function completePopupAuthorization(data, source) {
    if (!popupAuth || popupAuth.consumed || source !== popupAuth.popup) return;
    popupAuth.consumed = true;
    const activePopup = popupAuth.popup;
    if (popupAuth.timer) window.clearInterval(popupAuth.timer);
    if (popupAuth.timeout) window.clearTimeout(popupAuth.timeout);
    popupAuth = null;
    try {
      if (!data || typeof data !== 'object') throw new Error('Favorites sign-in returned an invalid response.');
      const expectedState = sessionStorage.getItem(storageKeys.state);
      const verifier = sessionStorage.getItem(storageKeys.verifier);
      if (!data.state || data.state !== expectedState || !verifier) throw new Error('Shopify login state could not be verified.');
      const pending = JSON.parse(sessionStorage.getItem(storageKeys.pending) || 'null');
      if (data.error) throw new Error(data.error_description || 'Shopify login could not be completed.');
      if (!data.code) throw new Error('Shopify login did not return an authorization code.');
      [storageKeys.state, storageKeys.verifier, storageKeys.pending].forEach((key) => sessionStorage.removeItem(key));
      await exchangeCode(data.code, verifier);
      if (pending?.productGid) {
        await readFavorites();
        const next = new Set(favoriteGids);
        next.add(pending.productGid);
        await writeFavorites([...next], compareDigest);
      }
      try { activePopup.close(); } catch (_) {}
      busy = false;
      render();
    } catch (error) {
      try { activePopup.close(); } catch (_) {}
      clearPendingAuthorization();
      console.error('[CCA Favorites]', error);
      showError(error?.message || 'Favorites sign-in could not be completed.');
    }
  }

  window.addEventListener('message', (event) => {
    if (!popupAuth || event.origin !== window.location.origin || event.source !== popupAuth.popup || event.data?.type !== 'cca-favorites-oauth-callback') return;
    completePopupAuthorization(event.data, event.source);
  });

  async function handleCallback() {
    const params = new URLSearchParams(window.location.search);
    const storedCallback = sessionStorage.getItem(storageKeys.callback);
    let callback = null;
    if (storedCallback) {
      try { callback = JSON.parse(storedCallback); } catch { callback = null; }
      sessionStorage.removeItem(storageKeys.callback);
    }
    const code = callback?.code || params.get('code');
    const error = callback?.error || params.get('error');
    if (code || error) scrubOAuthParams();
    if (!code) {
      if (error) throw new Error(callback?.error_description || params.get('error_description') || 'Shopify login could not be completed.');
      return false;
    }
    const state = callback?.state || params.get('state');
    const expectedState = sessionStorage.getItem(storageKeys.state);
    const verifier = sessionStorage.getItem(storageKeys.verifier);
    if (!state || state !== expectedState || !verifier) throw new Error('Shopify login state could not be verified.');
    await exchangeCode(code, verifier);
    const pending = JSON.parse(sessionStorage.getItem(storageKeys.pending) || 'null');
    sessionStorage.removeItem(storageKeys.state);
    sessionStorage.removeItem(storageKeys.verifier);
    sessionStorage.removeItem(storageKeys.pending);
    if (pending?.productGid) {
      await readFavorites();
      const next = new Set(favoriteGids);
      next.add(pending.productGid);
      await writeFavorites([...next], compareDigest);
      window.location.replace(safeReturnPath(pending.returnPath));
      return true;
    }
    window.history.replaceState({}, '', config.callbackPath);
    return true;
  }

  async function init() {
    try {
      if (await handleCallback()) return;
      const storefrontHydrated = hydrateStorefrontFavorites();
      if (await accessToken()) await readFavorites();
      if (!storefrontHydrated && !tokenFromStorage() && !sessionStorage.getItem(storageKeys.refreshToken)) render();
    } catch (error) {
      console.error('[CCA Favorites]', error);
      showError(error?.message || 'Favorites are temporarily unavailable.');
    }
    render();
  }

  document.addEventListener('click', (event) => {
    const target = eventElement(event);
    const button = target?.closest('[data-cca-favorite]');
    const logoutLink = target?.closest('[data-cca-favorites-logout]');
    if (logoutLink && !button) { event.preventDefault(); logout(); return; }
    if (button) { event.preventDefault(); toggle(button); }
  });
  document.addEventListener('focusin', (event) => {
    const button = eventElement(event)?.closest('[data-cca-favorite]');
    if (button) showTooltip(button);
  });
  document.addEventListener('mouseenter', (event) => {
    const button = eventElement(event)?.closest('[data-cca-favorite]');
    if (button) showTooltip(button);
  }, true);
  document.addEventListener('focusout', (event) => {
    const button = eventElement(event)?.closest('[data-cca-favorite]');
    if (button && !button.contains(event.relatedTarget)) hideTooltip(button);
  });
  document.addEventListener('mouseleave', (event) => {
    const button = eventElement(event)?.closest('[data-cca-favorite]');
    if (button && document.activeElement !== button) hideTooltip(button);
  }, true);
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const button = eventElement(event)?.closest('[data-cca-favorite]');
    if (button) hideTooltip(button);
  });
  window.addEventListener('resize', repositionVisibleTooltips);
  window.addEventListener('scroll', repositionVisibleTooltips, true);
  window.addEventListener('cca:favorites:logout', logout);
  init();
})();
